import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

/*
 * Botão do GEF: o `button` do shadcn/ui com as ênfases do protótipo do PO.
 *
 * - `primary`: verde sólido de ação ("Iniciar comparação", "Área do Gestor").
 * - `secondary`: contorno verde sobre branco ("Satélite" desligado).
 * - `outline`: contorno neutro ("Filtros", os botões da barra do mapa).
 * - `ghost`: sem fundo nem borda, para ícone ou ação discreta.
 * - `link`: texto no azul dos links ("Selecionar todas").
 * - `destructive`: vermelho, para excluir ou descartar.
 *
 * Cantos de 8px e texto de 14px em negrito em todas as ênfases, como no
 * protótipo. O foco visível é o global (global.css): o botão não o redesenha.
 */
const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2',
    'rounded-lg border border-transparent whitespace-nowrap select-none',
    'text-base font-bold',
    'transition-colors duration-(--gef-duracao-rapida)',
    'disabled:cursor-not-allowed disabled:opacity-(--gef-desabilitado)',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground [&:not(:disabled):hover]:bg-primary-hover',
        secondary:
          'border-primary bg-secondary text-secondary-foreground [&:not(:disabled):hover]:bg-primary-soft',
        outline: 'border-border bg-background text-foreground [&:not(:disabled):hover]:bg-muted',
        ghost: 'bg-transparent text-current [&:not(:disabled):hover]:bg-muted',
        link: 'h-auto bg-transparent px-0 text-sm font-medium text-link underline-offset-4 [&:not(:disabled):hover]:underline',
        destructive: 'bg-destructive text-inverse [&:not(:disabled):hover]:bg-destructive/90',
      },
      size: {
        /** 28px: os botões pequenos da barra sobre o mapa ("Brasil", "Tela cheia"). */
        sm: 'h-7 rounded-md px-2 text-xs',
        /** 36px: o tamanho padrão do protótipo. */
        md: 'h-9 px-4',
        /** Quadrado de 36px, só com ícone: o nome acessível vai em `aria-label`. */
        icon: 'size-9 px-0',
      },
      block: { true: 'w-full' },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({ className, variant, size, block, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, block, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
